import React, { useEffect, useRef, useCallback } from 'react';

function buildSchema(data) {
  // Support both array-of-tables and object with .tables
  const tables = Array.isArray(data) ? data : (data.tables || []);
  const COLORS = ['#38bdf8','#818cf8','#34d399','#fb923c','#f472b6','#a78bfa','#fbbf24','#2dd4bf','#f87171','#e879f9'];
  const ICONS  = ['📋','🔬','⚕️','💊','🧪','❤️','📊','🗂️','🔗','📌'];
  return tables.map((t, i) => ({
    name: t.table_name || t.name || `table_${i}`,
    desc: t.description || '',
    color: COLORS[i % COLORS.length],
    icon:  ICONS[i % ICONS.length],
    cols: (t.columns || t.cols || []).map(c => ({
      n:  c.name || c.n || '',
      t:  c.data_type || c.type || c.t || '',
      pk: !!(c.pk || (c.name || '').endsWith('_id') && i === 0),
      fk: c.relationship_hint || c.fk || null,
    })),
  }));
}

function buildRels(schema) {
  const rels = [];
  const names = new Set(schema.map(t => t.name));
  schema.forEach(t => {
    const seen = new Set();
    t.cols.forEach(c => {
      if (c.fk && names.has(c.fk) && !seen.has(c.fk)) {
        seen.add(c.fk);
        rels.push({ from: t.name, to: c.fk });
      }
    });
  });
  return rels;
}

function autoLayout(schema, vpW = 1200, vpH = 700) {
  const n = schema.length;
  const cx = vpW / 2;
  const cy = vpH / 2;
  const rx = Math.max(280, Math.min(vpW * 0.38, 520));
  const ry = Math.max(180, Math.min(vpH * 0.38, 300));
  const pos = {};
  schema.forEach((t, i) => {
    const angle = (2 * Math.PI * i / n) - Math.PI / 2;
    pos[t.name] = { x: cx + rx * Math.cos(angle) - 110, y: cy + ry * Math.sin(angle) - 90 };
  });
  return pos;
}

export default function ERDCanvas({ data }) {
  const canvasRef   = useRef(null);
  const worldRef    = useRef(null);
  const stateRef    = useRef({ panX: 0, panY: 0, scale: 1, active: null, positions: {}, cards: {}, schema: [], rels: [] });

  const drawLines = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { panX, panY, scale, active, positions, cards, rels } = stateRef.current;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Read theme-aware colors from CSS variables
    const cs = getComputedStyle(document.documentElement);
    const gridColor   = cs.getPropertyValue('--grid-dot').trim() || '#1a2235';
    const accentColor = cs.getPropertyValue('--accent').trim()   || '#38bdf8';

    // dot grid
    ctx.fillStyle = gridColor;
    const gs = 32 * scale;
    const ox = ((panX % gs) + gs) % gs;
    const oy = ((panY % gs) + gs) % gs;
    for (let x = ox; x < W; x += gs)
      for (let y = oy; y < H; y += gs) {
        ctx.beginPath(); ctx.arc(x, y, 0.8, 0, Math.PI * 2); ctx.fill();
      }

    rels.forEach(rel => {
      const fp_pos = positions[rel.from];
      const tp_pos = positions[rel.to];
      const fe = cards[rel.from];
      const te = cards[rel.to];
      if (!fp_pos || !tp_pos || !fe || !te) return;

      const fcx = panX + (fp_pos.x + fe.w / 2) * scale;
      const fcy = panY + (fp_pos.y + fe.h / 2) * scale;
      const tcx = panX + (tp_pos.x + te.w / 2) * scale;
      const tcy = panY + (tp_pos.y + te.h / 2) * scale;

      const edge = (pos, card, toward) => {
        const cx2 = panX + (pos.x + card.w / 2) * scale;
        const cy2 = panY + (pos.y + card.h / 2) * scale;
        const dx = toward.x - cx2, dy = toward.y - cy2;
        if (!dx && !dy) return { x: cx2, y: cy2 };
        const hw = (card.w / 2) * scale, hh = (card.h / 2) * scale;
        const t = Math.min(Math.abs(hw / dx), Math.abs(hh / dy));
        return { x: cx2 + dx * t, y: cy2 + dy * t };
      };

      const fp = edge(fp_pos, fe, { x: tcx, y: tcy });
      const tp = edge(tp_pos, te, { x: fcx, y: fcy });
      const isActive = active && (rel.from === active || rel.to === active);

      const cpx1 = fp.x + (tp.x - fp.x) * 0.45;
      const cpy1 = fp.y;
      const cpx2 = tp.x - (tp.x - fp.x) * 0.45;
      const cpy2 = tp.y;

      ctx.save();
      ctx.strokeStyle = accentColor;
      ctx.lineWidth   = isActive ? 2.5 : 1.5;
      ctx.setLineDash(isActive ? [] : [5, 4]);
      ctx.globalAlpha = isActive ? 1 : 0.28;
      if (isActive) { ctx.shadowColor = accentColor; ctx.shadowBlur = 10; }
      ctx.beginPath();
      ctx.moveTo(fp.x, fp.y);
      ctx.bezierCurveTo(cpx1, cpy1, cpx2, cpy2, tp.x, tp.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = isActive ? 1 : 0.4;
      [fp, tp].forEach(pt => {
        ctx.beginPath(); ctx.arc(pt.x, pt.y, isActive ? 4.5 : 3, 0, Math.PI * 2);
        ctx.fillStyle = accentColor; ctx.fill();
      });
      ctx.restore();
    });
  }, []);

  useEffect(() => {
    if (!data) return;
    const schema = buildSchema(data);
    const rels   = buildRels(schema);

    // Center auto-layout based on actual viewport size if available
    const vp = canvasRef.current?.parentElement;
    const vpW = vp?.clientWidth  || 1200;
    const vpH = vp?.clientHeight || 700;
    const initPos = autoLayout(schema, vpW, vpH);

    stateRef.current.schema = schema;
    stateRef.current.rels   = rels;
    stateRef.current.positions = initPos;
    stateRef.current.cards = {};
    stateRef.current.active = null;
    stateRef.current.panX = 0;
    stateRef.current.panY = 0;
    stateRef.current.scale = 1;

    // Ensure canvas dimensions are correct when data arrives
    if (canvasRef.current && vp) {
      canvasRef.current.width  = vp.clientWidth;
      canvasRef.current.height = vp.clientHeight;
    }

    // Build DOM cards
    const world = worldRef.current;
    if (!world) return;
    world.innerHTML = '';

    schema.forEach(t => {
      const pos = initPos[t.name] || { x: 0, y: 0 };
      const card = document.createElement('div');
      card.style.cssText = `position:absolute;left:${pos.x}px;top:${pos.y}px;min-width:210px;background:#131c2e;border:1px solid #1e293b;border-radius:10px;box-shadow:0 4px 24px rgba(0,0,0,.5);cursor:pointer;transition:border-color .15s,box-shadow .15s;`;

      const head = document.createElement('div');
      head.style.cssText = `padding:9px 12px 8px;border-bottom:1px solid #1e293b;display:flex;align-items:center;gap:8px;cursor:grab;border-left:3px solid ${t.color};border-radius:9px 9px 0 0;`;
      head.innerHTML = `<span style="font-size:13px">${t.icon}</span><span style="font-family:Syne,sans-serif;font-size:11px;font-weight:700;color:#e2e8f0;letter-spacing:.03em">${t.name}</span>`;
      card.appendChild(head);

      if (t.desc) {
        const desc = document.createElement('div');
        desc.style.cssText = 'padding:3px 12px;font-size:9px;color:#64748b;font-style:italic;border-bottom:1px solid #1e293b;';
        desc.textContent = t.desc;
        card.appendChild(desc);
      }

      const cols = document.createElement('div');
      cols.style.cssText = 'padding:4px 0 5px;';
      t.cols.forEach(c => {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;padding:2px 12px;gap:5px;';
        const badge = c.pk
          ? `<span style="font-size:8px;padding:1px 5px;border-radius:3px;font-weight:700;letter-spacing:.05em;background:rgba(251,191,36,.12);color:#fbbf24;border:1px solid rgba(251,191,36,.3);width:24px;text-align:center;flex-shrink:0">PK</span>`
          : c.fk
          ? `<span style="font-size:8px;padding:1px 5px;border-radius:3px;font-weight:700;letter-spacing:.05em;background:rgba(244,114,182,.1);color:#f472b6;border:1px solid rgba(244,114,182,.25);width:24px;text-align:center;flex-shrink:0">FK</span>`
          : `<span style="width:24px;flex-shrink:0"></span>`;
        row.innerHTML = `${badge}<span style="font-size:10px;color:#e2e8f0;flex:1">${c.n}</span><span style="font-size:8px;color:#64748b">${c.t}</span>`;
        cols.appendChild(row);
      });
      card.appendChild(cols);
      world.appendChild(card);

      // Measure after append
      requestAnimationFrame(() => {
        stateRef.current.cards[t.name] = { w: card.offsetWidth, h: card.offsetHeight };
        drawLines();
      });

      // Drag
      let dragging = false, ox = 0, oy = 0;
      head.addEventListener('mousedown', e => {
        e.stopPropagation();
        dragging = true;
        const r = card.getBoundingClientRect();
        ox = (e.clientX - r.left) / stateRef.current.scale;
        oy = (e.clientY - r.top)  / stateRef.current.scale;
        card.style.zIndex = 50;
        stateRef.current.active = t.name;
        world.querySelectorAll('[data-active]').forEach(el => el.removeAttribute('data-active'));
        card.setAttribute('data-active', '1');
        card.style.borderColor = '#38bdf8';
        drawLines();
        const onMove = ev => {
          if (!dragging) return;
          const { panX, panY, scale } = stateRef.current;
          const wx = (ev.clientX - panX) / scale - ox;
          const wy = (ev.clientY - panY - 50) / scale - oy;
          stateRef.current.positions[t.name] = { x: wx, y: wy };
          card.style.left = wx + 'px';
          card.style.top  = wy + 'px';
          stateRef.current.cards[t.name] = { w: card.offsetWidth, h: card.offsetHeight };
          drawLines();
        };
        const onUp = () => {
          dragging = false;
          card.style.zIndex = '';
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });
      card.addEventListener('click', () => {
        stateRef.current.active = stateRef.current.active === t.name ? null : t.name;
        world.querySelectorAll('div[data-card]').forEach(el => { el.style.borderColor = '#1e293b'; el.removeAttribute('data-active'); });
        if (stateRef.current.active === t.name) { card.style.borderColor = '#38bdf8'; card.setAttribute('data-active','1'); }
        drawLines();
      });
      card.setAttribute('data-card', t.name);
    });

    setTimeout(drawLines, 100);
  }, [data, drawLines]);

  // Canvas resize + pan/zoom
  useEffect(() => {
    const vp = canvasRef.current?.parentElement;
    if (!vp) return;
    const resize = () => {
      canvasRef.current.width  = vp.clientWidth;
      canvasRef.current.height = vp.clientHeight;
      drawLines();
    };
    resize();
    window.addEventListener('resize', resize);

    const onWheel = e => {
      e.preventDefault();
      const f = e.deltaY > 0 ? 0.92 : 1.08;
      const mx = e.clientX, my = e.clientY - 50;
      const s = stateRef.current;
      const wx = (mx - s.panX) / s.scale;
      const wy = (my - s.panY) / s.scale;
      s.scale = Math.max(0.25, Math.min(2.5, s.scale * f));
      s.panX = mx - wx * s.scale;
      s.panY = my - wy * s.scale;
      worldRef.current.style.transform = `translate(${s.panX}px,${s.panY}px) scale(${s.scale})`;
      drawLines();
    };
    vp.addEventListener('wheel', onWheel, { passive: false });

    let isPan = false, ps = {}, po = {};
    const onDown = e => {
      if (e.target !== vp && e.target !== canvasRef.current) return;
      isPan = true; ps = { x: e.clientX, y: e.clientY }; po = { x: stateRef.current.panX, y: stateRef.current.panY };
      vp.style.cursor = 'grabbing';
    };
    const onMove = e => {
      if (!isPan) return;
      stateRef.current.panX = po.x + (e.clientX - ps.x);
      stateRef.current.panY = po.y + (e.clientY - ps.y);
      worldRef.current.style.transform = `translate(${stateRef.current.panX}px,${stateRef.current.panY}px) scale(${stateRef.current.scale})`;
      drawLines();
    };
    const onUp = () => { isPan = false; vp.style.cursor = 'grab'; };
    vp.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('resize', resize);
      vp.removeEventListener('wheel', onWheel);
      vp.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [drawLines]);

  if (!data) return <div style={{ padding: 40, color: 'var(--muted)', fontSize: 12 }}>Select a dataset to view the ERD.</div>;

  // The ERD canvas uses its own fixed dark theme regardless of app theme,
  // similar to Figma / draw.io — keeps the visualization consistent.
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', cursor: 'grab', background: '#0b0f1a' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }} />
      <div ref={worldRef} style={{ position: 'absolute', top: 0, left: 0, transformOrigin: '0 0', zIndex: 2 }} />
      <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: '#64748b', background: 'rgba(11,15,26,.85)', padding: '4px 14px', borderRadius: 99, border: '1px solid #1e293b', pointerEvents: 'none', letterSpacing: '0.06em', zIndex: 10 }}>
        Drag header to move · Scroll to zoom · Click table to highlight relations
      </div>
      <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(17,24,39,.92)', border: '1px solid #1e293b', borderRadius: 8, padding: '10px 14px', zIndex: 10, fontSize: 9 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, color: '#38bdf8', letterSpacing: '.1em', marginBottom: 6 }}>LEGEND</div>
        {[['#fbbf24','rgba(251,191,36,.12)','PK — Primary Key'],['#f472b6','rgba(244,114,182,.1)','FK — Foreign Key']].map(([c,bg,l]) => (
          <div key={l} style={{ display:'flex',alignItems:'center',gap:7,marginBottom:4 }}>
            <span style={{ background:bg, border:`1px solid ${c}`, borderRadius:3, fontSize:7, padding:'1px 4px', color:c, fontWeight:700 }}>{l.slice(0,2)}</span>
            <span style={{ color:'#64748b' }}>{l.slice(5)}</span>
          </div>
        ))}
        <div style={{ display:'flex',alignItems:'center',gap:7,marginTop:2 }}>
          <div style={{ width:22,height:2,borderTop:'1.5px dashed #38bdf8',opacity:.5 }}/>
          <span style={{ color:'#64748b' }}>FK Relation</span>
        </div>
      </div>
    </div>
  );
}
